import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { Button } from '@common/Button/Button';
import { Icon, IconID } from '@common/Icon/Icon';
import type { NodeContact } from '@models/api/node-contact.types';
import appendQuery from 'append-query';
import { useRouter } from 'next/router';
import { getLocale } from '@libs/constants';
import { IGUS_REWRITE } from '@libs/redirect.helper';
import classNames from 'classnames';

// adre query param needs to be appended to this for the default igus contact form
const DEFAULT_CONTACT_FORM_RELATIVE_URL = `/myigus/default.aspx?page=form_contact`;

export type ContactBoxProps = Omit<NodeContact, 'email'>;

export const ContactBox = ({ id, name, picture, phone, contactFormUrl }: ContactBoxProps) => {
  const { t } = useTranslation('layout/contactBox');
  const router = useRouter();
  const locale = getLocale(router);
  const defaultContactFormUrl = IGUS_REWRITE(DEFAULT_CONTACT_FORM_RELATIVE_URL, locale);
  const actualContactFormUrl = contactFormUrl || appendQuery(defaultContactFormUrl, { adre: `${id}` });

  const times = () => {
    const chatTimes = t('CONTACT_BOX.CHAT_TIMES');
    const whatsappTimes = t('CONTACT_BOX.WHATSAPP_TIMES');
    const onlineTimes = t('CONTACT_BOX.ONLINE_TIMES');
    const onlineCustomLinkText = t('CONTACT_BOX.ONLINE_CUSTOM_LINK_TEXT');
    const onlineCustomLinkHref = t('CONTACT_BOX.ONLINE_CUSTOM_LINK_HREF');

    return (
      // TODO at some point this should be translated and/or coming from a CMS (or similar)
      <div className="mt-6 leading-9 text-sm">
        <div className="mb-5 font-semibold">{t('CONTACT_BOX.PERSONAL')}:</div>
        <div className="mb-5">
          <span
            dangerouslySetInnerHTML={
              // dangerouslySetInnerHTML because the translated content can include <br> tags
              { __html: t('CONTACT_BOX.PERSONAL_TIMES') }
            }
          />
        </div>
        <div data-testid="contact-box-online" className={classNames(['font-semibold', !onlineTimes && ' mb-5'])}>
          {t('CONTACT_BOX.ONLINE')}:
        </div>
        {onlineCustomLinkText && onlineCustomLinkHref && (
          <div data-testid="contact-box-online-custom-link" className="mb-5">
            <a target="blank" className="text-igus-primary" href={onlineCustomLinkHref}>
              {onlineCustomLinkText}
            </a>
          </div>
        )}
        {onlineTimes ? (
          <div data-testid="contact-box-online-times" dangerouslySetInnerHTML={{ __html: onlineTimes }} />
        ) : (
          <div>
            <div className="flex">
              {chatTimes && (
                <div className="flex-col">
                  <div
                    data-testid="contact-box-chat"
                    className="font-semibold"
                    dangerouslySetInnerHTML={{ __html: t('CONTACT_BOX.CHAT') }}
                  />
                  <div data-testid="contact-box-chat-times" dangerouslySetInnerHTML={{ __html: chatTimes }} />
                </div>
              )}
              {whatsappTimes && (
                <div className="flex-col pl-20">
                  <div
                    data-testid="contact-box-whatsapp"
                    className="font-semibold"
                    dangerouslySetInnerHTML={{ __html: t('CONTACT_BOX.WHATSAPP') }}
                  />
                  <div data-testid="contact-box-whatsapp-times" dangerouslySetInnerHTML={{ __html: whatsappTimes }} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {name && (
        <div data-testid="contact-box" className="mt-20">
          <h2 className="bg-igus-primary text-white font-medium px-8 py-4 lg:mb-12">{t('CONTACT_BOX.HEADLINE')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-12 contact-box-grid">
            <div className="bg-gray-100 p-8">
              <h3 className="font-medium">{t('CONTACT_BOX.PERSONAL_CONTACT')}</h3>
              <div className="flex flex-col sm:flex-row mt-6">
                <div>
                  <Image unoptimized src={picture} alt={name} width={135} height={135} />
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-12">
                  <span className="text-sm font-semibold">{name}</span>
                  <p className="flex my-5">
                    <Icon primary={false} id={IconID.PHONE} size={6} />
                    <a className="ml-4 -mt-2 text-2xl" href={`tel:${phone}`}>
                      {phone}
                    </a>
                  </p>
                  <Button
                    href={actualContactFormUrl}
                    text={t('CONTACT_BOX.EMAIL')}
                    className="w-full sm:w-72 lg:w-auto"
                  />
                </div>
              </div>
            </div>
            <div className="bg-gray-100 p-8">
              <h3 className="font-medium">{t('CONTACT_BOX.DELIVERY_AND_CONSULTING')}</h3>
              {times()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
